import { useState, type ChangeEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import type { RestoreDryRunReport } from "@victor/types";
import { api } from "../../../lib/api";
import { Button } from "../../../ui/Button";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { Badge, Panel, SectionTitle } from "./_ui";

type Props = { tenantId: string; raisonSociale: string };

export function BackupTab({ tenantId, raisonSociale }: Props) {
  const [archiveB64, setArchiveB64] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [report, setReport] = useState<RestoreDryRunReport | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const token =
    typeof window !== "undefined" ? window.__victorAdminToken ?? "" : "";

  const dryRunMut = useMutation({
    mutationFn: () => {
      if (!archiveB64) throw new Error("Aucune archive sélectionnée.");
      return api.backup.restoreJson(tenantId, {
        archive: archiveB64,
        dryRun: true,
        confirm: "",
      });
    },
    onSuccess: (r) => setReport(r),
  });

  const restoreMut = useMutation({
    mutationFn: () => {
      if (!archiveB64) throw new Error("Aucune archive sélectionnée.");
      return api.backup.restoreJson(tenantId, {
        archive: archiveB64,
        dryRun: false,
        confirm: `CONFIRMER RESTORE TENANT ${raisonSociale}`,
      });
    },
    onSuccess: () => {
      setConfirmOpen(false);
      window.alert("Restore exécuté avec succès.");
    },
  });

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    const text = await file.text();
    setArchiveB64(btoa(unescape(encodeURIComponent(text))));
    setReport(null);
  }

  // Pour le ZIP et le JSON on doit attacher le bearer manuellement ; on
  // utilise une fetch directe + window.open avec un blob.
  async function download(url: string, defaultName: string) {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    if (!res.ok) {
      window.alert(`Échec téléchargement (${res.status}).`);
      return;
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const dispo = res.headers.get("content-disposition") ?? "";
    const m = dispo.match(/filename="([^"]+)"/);
    a.download = m?.[1] ?? defaultName;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-5">
      <Panel>
        <SectionTitle
          icon={ICON.download}
          desc="Téléchargements à usage SAV / sauvegarde / migration. Les actions sont tracées dans l'audit du tenant."
        >
          Backup
        </SectionTitle>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() =>
              void download(api.backup.pdfsUrl(tenantId), "backup-pdfs.zip")
            }
          >
            Télécharger ZIP PDFs
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              void download(api.backup.jsonUrl(tenantId), "dump.json")
            }
          >
            Télécharger dump JSON
          </Button>
        </div>
      </Panel>

      <Panel>
        <SectionTitle
          icon={ICON.restore}
          desc={
            <>
              Réinjection des données depuis un dump JSON précédemment
              téléchargé. Toujours faire un <strong>dry-run</strong> avant
              l'exécution.
            </>
          }
        >
          Restore JSON
        </SectionTitle>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-navy-500">
              Sélectionner le fichier JSON
            </span>
            <input
              type="file"
              accept="application/json"
              onChange={handleFile}
              className="mt-1.5 block w-full rounded-xl text-sm text-navy-700 ring-1 ring-navy-100 file:mr-3 file:cursor-pointer file:border-0 file:bg-navy-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-navy-700 hover:file:bg-navy-100"
            />
            {filename ? (
              <p className="mt-1 text-[11px] text-navy-400">{filename}</p>
            ) : null}
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => dryRunMut.mutate()}
              disabled={!archiveB64 || dryRunMut.isPending}
            >
              {dryRunMut.isPending ? "Analyse…" : "Dry-run"}
            </Button>
            <Button
              variant="danger"
              onClick={() => setConfirmOpen(true)}
              disabled={!archiveB64 || !report || report.conflits.length > 0}
            >
              Exécuter le restore
            </Button>
          </div>
          {dryRunMut.error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {dryRunMut.error instanceof ApiClientError
                ? dryRunMut.error.message
                : "Erreur."}
            </p>
          ) : null}
          {report ? (
            <div className="rounded-xl bg-navy-50/60 p-4 ring-1 ring-navy-100 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-navy-500">
                  Résultat dry-run
                </p>
                {report.ok ? (
                  <Badge tone="emerald" dot>
                    OK
                  </Badge>
                ) : (
                  <Badge tone="amber" dot>
                    {report.conflits.length} conflit(s)
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Object.entries(report.counts).map(([k, v]) => (
                  <div
                    key={k}
                    className="rounded-lg bg-white px-3 py-2 ring-1 ring-navy-100"
                  >
                    <p className="text-[11px] text-navy-500">{k}</p>
                    <p className="text-lg font-black tracking-tight text-navy-800">
                      {v}
                    </p>
                  </div>
                ))}
              </div>
              {report.conflits.length > 0 ? (
                <ul className="max-h-40 overflow-auto rounded-lg bg-white p-2 text-[11px] text-amber-800 ring-1 ring-amber-200">
                  {report.conflits.slice(0, 20).map((c, i) => (
                    <li key={i}>
                      <code>
                        {c.table}/{c.id}
                      </code>{" "}
                      · {c.motif}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </Panel>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Exécuter le restore ?"
        description={
          <p>
            Cette opération va upserter les données du dump dans le tenant{" "}
            <strong>{raisonSociale}</strong>. Action <strong>tracée</strong> en
            audit et irréversible.
          </p>
        }
        requireType={`CONFIRMER RESTORE TENANT ${raisonSociale}`}
        confirmLabel="Exécuter le restore"
        destructive
        onConfirm={() => restoreMut.mutate()}
        busy={restoreMut.isPending}
      />
      {restoreMut.error ? (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {restoreMut.error instanceof ApiClientError
            ? restoreMut.error.message
            : "Erreur."}
        </p>
      ) : null}
    </div>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  download: (
    <svg {...sp}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  ),
  restore: (
    <svg {...sp}>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  ),
};
